import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, FileText, Loader2, Upload, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import portalApi from "@/lib/portalApi";
import { useStartupPortalAuth } from "@/contexts/StartupPortalAuthContext";

const MAX_MB = 25;

const formatBytes = (b) => {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
};

const formatDate = (iso, lang = "de") => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(lang === "en" ? "en-US" : "de-DE", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

const AI_BADGE = {
  pending: "bg-sandBeige/60 text-cocoa",
  in_progress: "bg-lavenderFog text-cocoa",
  completed: "bg-sageMint text-cocoa",
  failed: "bg-roseMilk text-coral",
};

export default function PortalDecks() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "de";
  const { refreshSession } = useStartupPortalAuth();
  const [decks, setDecks] = useState([]);
  const [currentVersion, setCurrentVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [changeNote, setChangeNote] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await portalApi.get("/decks");
      setDecks(data.decks || []);
      setCurrentVersion(data.current_version || 0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type && f.type !== "application/pdf") {
      toast.error(t("portal.decks.errorType"));
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(t("portal.decks.errorSize", { mb: MAX_MB }));
      return;
    }
    setSelectedFile(f);
    setUploadOpen(true);
  };

  const submit = async () => {
    if (!selectedFile) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", selectedFile);
      if (changeNote.trim()) fd.append("change_note", changeNote.trim());
      await portalApi.post("/decks", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(t("portal.decks.uploadSuccess"));
      setUploadOpen(false);
      setSelectedFile(null);
      setChangeNote("");
      if (fileRef.current) fileRef.current.value = "";
      await Promise.all([load(), refreshSession()]);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) toast.error(t("portal.decks.rateLimited"));
      else if (status === 413) toast.error(t("portal.decks.errorSize", { mb: MAX_MB }));
      else toast.error(t("portal.decks.uploadFailed"));
    } finally {
      setUploading(false);
    }
  };

  const onDownload = async (deck) => {
    try {
      const res = await portalApi.get(`/decks/${deck.id}/download`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = deck.filename || `pitch_v${deck.version}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (_e) {
      toast.error(t("portal.decks.downloadFailed"));
    }
  };

  return (
    <div className="max-w-4xl mx-auto flex flex-col gap-6" data-testid="portal-decks">
      <motion.header
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3"
      >
        <div>
          <p className="wit-eyebrow text-raspberry mb-2">{t("portal.decks.eyebrow")}</p>
          <h1 className="font-serif text-3xl sm:text-4xl text-cocoa leading-[1.05]">
            {t("portal.decks.title")}
          </h1>
          <p className="mt-3 text-warmGrey max-w-xl">{t("portal.decks.lead")}</p>
        </div>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="bg-raspberry hover:bg-coral text-white text-sm rounded-full px-5 py-3 inline-flex items-center gap-2 transition-colors"
          data-testid="decks-upload-btn"
        >
          <Upload size={14} /> {t("portal.decks.newVersion")}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="application/pdf"
          onChange={onPickFile}
          className="hidden"
          data-testid="decks-file-input"
        />
      </motion.header>

      {loading ? (
        <div className="rounded-3xl border border-sandBeige bg-white p-8 flex items-center justify-center text-warmGrey gap-2">
          <Loader2 className="animate-spin" size={18} /> {t("portal.decks.loading")}
        </div>
      ) : decks.length === 0 ? (
        <div className="rounded-3xl border border-sandBeige bg-white p-10 text-center" data-testid="decks-empty">
          <FileText size={28} className="mx-auto text-warmGrey" />
          <p className="font-serif text-2xl text-cocoa mt-4">{t("portal.decks.emptyTitle")}</p>
          <p className="text-warmGrey text-sm mt-2">{t("portal.decks.emptyBody")}</p>
        </div>
      ) : (
        <ul className="flex flex-col gap-3" data-testid="decks-list">
          {decks.map((d, i) => {
            const isCurrent = d.version === currentVersion;
            const badge = AI_BADGE[d.analysis_status] || AI_BADGE.pending;
            return (
              <motion.li
                key={d.id}
                initial={{ opacity: 0, y: 6 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 0.3, ease: "easeOut", delay: i * 0.04 }}
                className="rounded-3xl border border-sandBeige bg-white p-5 sm:p-6 shadow-soft flex flex-col sm:flex-row sm:items-center gap-4"
                data-testid={`deck-row-${d.version}`}
              >
                <div className="w-12 h-12 rounded-2xl bg-sageMint/70 text-cocoa flex items-center justify-center shrink-0">
                  <FileText size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-cocoa">
                      {t("portal.decks.versionLabel")} {d.version}
                    </p>
                    {isCurrent && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide bg-raspberry text-white">
                        {t("portal.decks.current")}
                      </span>
                    )}
                    <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide ${badge}`}>
                      {t(`portal.decks.aiStatus.${d.analysis_status}`)}
                    </span>
                  </div>
                  <p className="text-sm text-warmGrey truncate mt-1">{d.filename}</p>
                  <p className="text-xs text-warmGrey mt-1">
                    {formatDate(d.uploaded_at, lang)} · {formatBytes(d.size)}{" "}
                    {d.page_count ? `· ${d.page_count} ${t("portal.decks.pages")}` : ""}
                  </p>
                  {d.change_note && (
                    <p className="text-xs text-cocoa italic mt-2 max-w-xl">"{d.change_note}"</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => onDownload(d)}
                  className="self-start sm:self-center text-sm text-cocoa hover:text-raspberry bg-ivory hover:bg-roseMilk px-4 py-2 rounded-full inline-flex items-center gap-2 transition-colors"
                  data-testid={`deck-download-${d.version}`}
                >
                  <Download size={14} /> {t("portal.decks.download")}
                </button>
              </motion.li>
            );
          })}
        </ul>
      )}

      {/* Upload Modal */}
      <AnimatePresence>
        {uploadOpen && selectedFile && (
          <motion.div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="decks-upload-modal"
          >
            <div
              className="absolute inset-0 bg-cocoa/40 backdrop-blur-sm"
              onClick={() => !uploading && setUploadOpen(false)}
              aria-hidden="true"
            />
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 30, opacity: 0 }}
              transition={{ type: "spring", stiffness: 280, damping: 28 }}
              className="relative w-full max-w-md bg-white rounded-3xl border border-sandBeige p-6 sm:p-7 shadow-card"
            >
              <button
                type="button"
                onClick={() => setUploadOpen(false)}
                disabled={uploading}
                className="absolute top-4 right-4 text-warmGrey hover:text-cocoa"
                data-testid="upload-modal-close"
              >
                <X size={18} />
              </button>
              <p className="wit-eyebrow text-raspberry mb-2">
                {t("portal.decks.uploadTitle")}
              </p>
              <h3 className="font-serif text-2xl text-cocoa mb-1">
                {t("portal.decks.uploadVersionTitle", { v: currentVersion + 1 })}
              </h3>
              <div className="mt-4 rounded-2xl bg-ivory border border-sandBeige p-4 flex items-center gap-3">
                <FileText size={18} className="text-cocoa shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-cocoa truncate">{selectedFile.name}</p>
                  <p className="text-xs text-warmGrey">{formatBytes(selectedFile.size)}</p>
                </div>
              </div>
              <label className="mt-5 flex flex-col gap-2 text-sm text-cocoa">
                <span className="font-medium">{t("portal.decks.changeNoteLabel")}</span>
                <textarea
                  value={changeNote}
                  onChange={(e) => setChangeNote(e.target.value)}
                  rows={3}
                  placeholder={t("portal.decks.changeNotePlaceholder")}
                  className="bg-porcelain border border-sandBeige rounded-2xl px-4 py-3 focus:border-raspberry focus:outline-none text-sm resize-none transition-colors"
                  data-testid="upload-change-note"
                />
                <span className="text-xs text-warmGrey">{t("portal.decks.changeNoteHint")}</span>
              </label>
              <div className="mt-6 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setUploadOpen(false)}
                  disabled={uploading}
                  className="text-sm text-warmGrey hover:text-cocoa px-4 py-2.5 rounded-full"
                  data-testid="upload-cancel"
                >
                  {t("portal.decks.cancel")}
                </button>
                <button
                  type="button"
                  onClick={submit}
                  disabled={uploading}
                  className="bg-raspberry hover:bg-coral disabled:opacity-60 disabled:hover:bg-raspberry text-white text-sm rounded-full px-5 py-2.5 inline-flex items-center gap-2 transition-colors"
                  data-testid="upload-confirm"
                >
                  {uploading ? (
                    <>
                      <Loader2 size={14} className="animate-spin" /> {t("portal.decks.uploading")}
                    </>
                  ) : (
                    <>
                      <Upload size={14} /> {t("portal.decks.uploadConfirm")}
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
